import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Spinner } from "react-bootstrap";
import { selectUserDropdowns } from "../../../redux/slices/dropdownSlice";
import useCriticalData from "../../../functions/useCriticalData";
import AdvancedActivityLog from "./AdvancedActivityLog";
import ActivityLogChangeLog from "./ActivityLogChangeLog";
import { usePermissions } from "../../../hooks/usePermissions";

const ActivityLogContainer = ({ showSaveNotificationMessage }) => {
  const [activeTab, setActiveTab] = useState('activityLog'); // Default to main activity log
  const { canView, isLoading: isPermissionsLoading } = usePermissions();

  // Load critical data including user dropdowns
  const { 
    isLoading, 
    initialized,
    errors,
    loadingStates
  } = useCriticalData({
    includeUserDropdowns: true,
    autoLoad: true
  });

  // Check user dropdowns from Redux
  const userDropdowns = useSelector(selectUserDropdowns);

  // Debug logging
  useEffect(() => {
    console.log('ActivityLogContainer - Critical Data Status:', {
      isLoading,
      initialized,
      errors,
      loadingStates,
      userDropdowns: {
        mechOptions: userDropdowns?.mechOptions?.length || 0,
        appsOptions: userDropdowns?.appsOptions?.length || 0,
        elecOptions: userDropdowns?.elecOptions?.length || 0,
        controlOptions: userDropdowns?.controlOptions?.length || 0,
        adminOptions: userDropdowns?.adminOptions?.length || 0,
        salesOptions: userDropdowns?.salesOptions?.length || 0,
      }
    });
  }, [isLoading, initialized, errors, loadingStates, userDropdowns]);

  // Check if user can see change log based on component policies.
  const canViewChangeLog = canView('apps.activity-log-change-log');

  useEffect(() => {
    if (!canViewChangeLog && activeTab === 'activityLogChangeLog') {
      setActiveTab('activityLog');
    }
  }, [activeTab, canViewChangeLog]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Show loading state while critical data loads
  // Note: Don't block on userDropdowns loading as it can cause infinite loops
  // The component will render with empty dropdowns initially if needed
  if (isLoading || isPermissionsLoading || !initialized) {
    return (
      <div className="p-3">
        <div className="text-center mb-3">
          <h1>Activity Log</h1>
        </div>
        <div className="text-center p-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">
            Loading activity log data...
          </p>
        </div>
      </div>
    );
  }

  // Show error if user dropdowns failed to load (critical for this component)
  if (errors.userDropdowns) {
    return (
      <div className="p-3">
        <div className="text-center mb-3">
          <h1>Activity Log</h1>
        </div>
        <div className="alert alert-danger m-3">
          Failed to load user data: {errors.userDropdowns}
          <br />
          <small>Please refresh the page to try again.</small>
        </div>
      </div>
    );
  }

  // Check if userDropdowns have data
  const hasUserDropdowns = userDropdowns && 
    Object.values(userDropdowns).some(arr => Array.isArray(arr) && arr.length > 0);

  if (initialized && !hasUserDropdowns) {
    console.warn('ActivityLogContainer: User dropdowns are empty after initialization');
  }

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'activityLog':
        return <AdvancedActivityLog showSaveNotificationMessage={showSaveNotificationMessage} />;
      case 'activityLogChangeLog':
        return <ActivityLogChangeLog />;
      default:
        return <AdvancedActivityLog showSaveNotificationMessage={showSaveNotificationMessage} />;
    }
  };

  return (
    <div className="p-3 apps-activity-log-page">
      <div className="text-center mb-3">
        <h1>Activity Log</h1>
      </div>

      {/* Navigation tabs */}
      <ul className="nav nav-tabs mb-3 apps-activity-tabs">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'activityLog' ? 'active' : ''}`}
            onClick={() => handleTabChange('activityLog')}
          >
            Activity Log
          </button>
        </li>

        {/* Activity Log Change Log - Admin only */}
        {canViewChangeLog && (
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'activityLogChangeLog' ? 'active' : ''}`}
              onClick={() => handleTabChange('activityLogChangeLog')}
            >
              Activity Log Change Log
            </button>
          </li>
        )}
      </ul>

      {/* Render the active tab */}
      <div className="tab-content">
        {renderActiveTab()}
      </div>
    </div>
  );
};

ActivityLogContainer.propTypes = {
  showSaveNotificationMessage: PropTypes.func.isRequired,
};

export default ActivityLogContainer;
